import React, { useEffect, useRef } from "react";
import interiorArt from "../assets/ride/auto-interior.png";
import worldArt from "../assets/ride/whitefield-world.png";
import trafficArt from "../assets/ride/street-life.png";
import frontAutoArt from "../assets/ride/oncoming-auto-front.png";
import { advanceRide, RIDE_TIMING } from "./rideTimeline";
import {
  createMotionWorld,
  projectPerspective,
  updateMotionWorld,
  VANISHING_POINT,
  visibleObjects,
} from "./perspectiveMotion";

const SPRITES = { auto: 0, scooter: 1, pedestrian: 4 };

export default function AnimatedStreet({
  state,
  paused,
  reducedMotion,
  motionTest = false,
  onEncounter,
  onApproach,
  onComplete,
}) {
  const canvas = useRef(null);
  const current = useRef(null);
  current.current = { state, paused, reducedMotion, motionTest, onEncounter, onApproach, onComplete };

  useEffect(() => {
    const element = canvas.current;
    const context = element.getContext("2d");
    const motionWorld = createMotionWorld();
    const images = [worldArt, trafficArt, interiorArt, frontAutoArt].map(source => {
      const image = new Image();
      image.src = source;
      return image;
    });
    const [worldImage, trafficImage, interiorImage, frontAutoImage] = images;
    const imageReady = image => image.complete && image.naturalWidth > 0;
    let frame;
    let previousStamp = 0;
    let width = 0;
    let height = 0;
    let timeline = 0;
    let previousState = "";
    let approached = false;
    let encountered = false;
    let completed = false;

    const resizeObserver = new ResizeObserver(() => {
      width = element.clientWidth;
      height = element.clientHeight;
      const density = Math.min(window.devicePixelRatio || 1, 1.5);
      element.width = width * density;
      element.height = height * density;
    });
    resizeObserver.observe(element);

    function drawSprite(object) {
      if (!imageReady(trafficImage)) return;
      const { x, y, scale, opacity } = object.screen;
      const index = SPRITES[object.type];
      context.save();
      context.globalAlpha = opacity;
      if (object.type === "auto" && imageReady(frontAutoImage)) {
        context.drawImage(frontAutoImage, x - scale / 2, y - scale * .97, scale, scale);
        context.restore();
        return;
      }
      context.translate(x, y - scale * .94);
      if (object.flip) context.scale(-1, 1);
      context.drawImage(
        trafficImage,
        (index % 3) * 512,
        Math.floor(index / 3) * 512,
        512,
        512,
        -scale / 2,
        0,
        scale,
        scale,
      );
      context.restore();
    }

    function drawRoadside(object) {
      const { x, y, scale, opacity } = object.screen;
      context.save();
      context.globalAlpha = opacity;
      context.translate(x, y);
      if (object.type === "tree") {
        context.fillStyle = "#274a38";
        context.fillRect(-scale * 0.045, -scale * 1.2, scale * 0.09, scale * 1.2);
        [[-0.22, -1.34, 0.27], [0.18, -1.38, 0.3], [0, -1.56, 0.31], [-0.05, -1.2, 0.34]].forEach(([x, y, radius], index) => {
          context.fillStyle = index % 2 ? "#3f7858" : "#2f6548";
          context.beginPath();
          context.arc(scale * x, scale * y, scale * radius, 0, Math.PI * 2);
          context.fill();
        });
      } else {
        context.strokeStyle = "#4a5f57";
        context.lineWidth = Math.max(1, scale * 0.032);
        context.beginPath();
        context.moveTo(0, 0);
        context.lineTo(0, -scale * 1.62);
        context.stroke();
        context.fillStyle = object.type === "sign" ? "#d46f52" : "#efd49b";
        context.fillRect(-scale * 0.16, -scale * 1.67, scale * 0.32, scale * 0.13);
      }
      context.restore();
    }

    function drawRoadMarkers(time) {
      for (let index = 0; index < 8; index += 1) {
        const depth = ((index / 8 - time * 0.028) % 1 + 1) % 1;
        const marker = projectPerspective({ depth, lane: 0, baseSize: 36 });
        context.save();
        context.globalAlpha = marker.opacity * 0.45;
        context.fillStyle = "#eadbbd";
        context.translate(marker.x, marker.y);
        context.rotate(-0.04);
        context.fillRect(-marker.scale * 0.05, -marker.scale * 0.55, marker.scale * 0.1, marker.scale * 0.65);
        context.restore();
      }
    }

    function drawInterior(running) {
      if (!imageReady(interiorImage)) return;
      const bounce = running ? Math.sin(motionWorld.time * 2.2) * 1.05 : 0;
      const lateral = running ? Math.sin(motionWorld.time * 1.35) * 1.1 : 0;
      const rotation = running ? Math.sin(motionWorld.time * 1.1) * 0.0015 : 0;
      context.save();
      context.translate(VANISHING_POINT.x, 450);
      context.rotate(rotation);
      context.translate(-VANISHING_POINT.x + lateral, -450 + bounce);
      context.drawImage(interiorImage, 0, 0, 1440, 900);
      context.restore();
    }

    function render(stamp) {
      frame = requestAnimationFrame(render);
      if (stamp - previousStamp < 16) return;
      const deltaTime = previousStamp ? Math.min((stamp - previousStamp) / 1000, 1) : 0;
      previousStamp = stamp;
      const props = current.current;
      if (!width || document.hidden) return;

      if (props.state !== previousState) {
        if (props.state === "WAITING") {
          Object.assign(motionWorld, createMotionWorld());
          timeline = 0;
          approached = false;
          encountered = false;
          completed = false;
        }
        previousState = props.state;
      }

      const running = !props.paused && !props.reducedMotion &&
        ["STARTING", "CRUISING", "APPROACHING"].includes(props.state);
      updateMotionWorld(motionWorld, deltaTime, running);
      if (props.motionTest && motionWorld.time >= 40 && !completed) {
        completed = true;
        props.onComplete?.();
      }

      if (!props.motionTest) {
        timeline = advanceRide(timeline, deltaTime, props.state, props.paused);
        if (timeline >= RIDE_TIMING.approach && !approached) { approached = true; props.onApproach?.(); }
        if (timeline >= RIDE_TIMING.discovery && !encountered) { encountered = true; props.onEncounter?.(); }
        if (timeline >= RIDE_TIMING.finish && !completed) { completed = true; props.onComplete?.(); }
      }

      const pixelRatio = element.width / width;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      context.clearRect(0, 0, width, height);
      const fit = Math.max(width / 1440, height / 900);
      context.translate((width - 1440 * fit) / 2, (height - 900 * fit) / 2);
      context.scale(fit, fit);
      context.fillStyle = "#a3b9a3";
      context.fillRect(0, 0, 1440, 900);

      if (imageReady(worldImage)) {
        const skylineZoom = 1 / (1 - Math.min(motionWorld.time, 40) * 0.012);
        context.save();
        context.translate(VANISHING_POINT.x, VANISHING_POINT.y);
        context.scale(skylineZoom, skylineZoom);
        context.translate(-VANISHING_POINT.x, -VANISHING_POINT.y);
        context.drawImage(worldImage, 0, 0, 1440, 900);
        context.restore();
      }

      drawRoadMarkers(motionWorld.time);
      visibleObjects(motionWorld).forEach(object => {
        if (object.type === "tree" || object.type === "pole" || object.type === "sign") drawRoadside(object);
        else drawSprite(object);
      });
      drawInterior(running);

      element.dataset.rideTime = motionWorld.time.toFixed(2);
      element.dataset.motionTest = props.motionTest ? "true" : "false";
      element.dataset.motionEvents = motionWorld.objects
        .map(object => `${object.id}:${object.finished ? "passed" : object.depth.toFixed(2)}`)
        .join(",");
    }

    frame = requestAnimationFrame(render);
    const resetClock = () => { previousStamp = 0; };
    document.addEventListener("visibilitychange", resetClock);
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      document.removeEventListener("visibilitychange", resetClock);
    };
  }, []);

  return (
    <canvas
      ref={canvas}
      className="ride-world"
      role="img"
      aria-label="Illustrated Whitefield through the auto with depth-based traffic and roadside movement."
    />
  );
}
